import React from 'react'
import { Container, Header, Message, Button, Icon } from 'semantic-ui-react'
import { toPairs, pathOr } from 'ramda'
import Link from 'next/link'
import { format } from 'date-fns'

import useAuthState from 'lib/hooks/useAuthState'
import SEO from 'lib/components/SEO'
import BasicDetails from 'lib/components/Recall/BasicDetails'
import CategoryIcon from 'lib/components/Recall/CategoryIcon'
import CategoryList from 'lib/components/Recall/CategoryList'

import Images from './Images'
import Products from './Products'
import Social from './Social'
import { Recall as RecallType } from '../../../../types/RM'

const TopIcon = ({ recall }: { recall: RecallType }) => {
  const alertType = recall.parsed.basic_details['Alert type']

  if (alertType === 'Information Update') {
    return <Icon circular name="info" />
  }
  if (
    alertType === 'Dear Healthcare Professional Letter' ||
    alertType === 'Notice to Hospitals'
  ) {
    return <Icon circular name="medkit" />
  }
  if (alertType === 'Advisory') {
    return <Icon circular name="warning" />
  }
  if (alertType === 'Public Communication') {
    return <Icon circular name="bullhorn" />
  }

  return <CategoryIcon category={recall.category[0]} circular />
}

const Recall = ({ recall }: { recall: RecallType }) => {
  const parsedPanels = Object.keys(recall.parsed)
  const unparsedPanels = recall.panels
    .filter(d => d)
    .filter(panel => !parsedPanels.includes(panel.panelName))
  const products = parsedPanels
    .filter(p => /^product_/.test(p))
    .map(key => recall.parsed[key])

  const [auth, loading] = useAuthState()

  const image = pathOr(
    'https://recallmonitor.ca/product-recall-image.jpg',
    ['images', 0, 'url'],
    recall.parsed,
  )
  const url = `https://recallmonitor.ca/recall/${recall.apiId}`

  return (
    <>
      <SEO
        title={`${recall.parsed.title} | Recall Monitor`}
        description={
          recall.parsed.basic_details['Alert type'] +
          ': ' +
          recall.parsed.title +
          '. Visit for more details and learn what to do.'
        }
        canonical={url}
        type="article"
        image={image}
      />
      <Container text css={{ marginTop: '1rem' }}>
        <Header as="h2" icon textAlign="center">
          <TopIcon recall={recall} />
          {recall.parsed.title}
          <Header.Subheader>
            <CategoryList categories={recall.category} />
          </Header.Subheader>
          <Header.Subheader>
            {format(new Date(recall.start_date * 1000), 'MMM do, y @ hh:mm a')}
          </Header.Subheader>
        </Header>
        <div style={{ textAlign: 'center' }}>
          <Social
            url={url}
            message={`#Recall #Alert: ${recall.parsed.title}`}
            image={image}
          />
        </div>

        {!auth && !loading && (
          <Message info>
            <Message.Header>Just found out about this recall?</Message.Header>
            <p>
              Get notified instantly when recalls like this are issued. Don't be
              at the mercy of a late news segment or a social share. Most
              recalls are not even mentioned in the mainstream!
            </p>
            <Link href="/signup" passHref>
              <Button as="a" primary>
                Sign up now to start protecting yourself
              </Button>
            </Link>
          </Message>
        )}

        <Header as="h3">Basic Details</Header>
        {/* @ts-ignore */}
        <div css={{ marginBottom: '1rem' }}>
          <BasicDetails data={recall.parsed.basic_details} />
        </div>

        {recall.parsed.summary && (
          <>
            <Header as="h3">Summary</Header>
            {toPairs(recall.parsed.summary).map(([key, val]) => (
              <React.Fragment key={key}>
                <Header as="h4">{key}</Header>
                <p>{val}</p>
              </React.Fragment>
            ))}
          </>
        )}

        {recall.parsed.images && (
          <>
            <Header as="h3">
              Images
              <Header.Subheader>Click to enlarge</Header.Subheader>
            </Header>
            <Images data={recall.parsed.images} />
          </>
        )}
      </Container>

      {products.length > 0 && (
        <>
          <Container text>
            <Header as="h3" css={{ marginTop: '1em !important' }}>
              Products
            </Header>
          </Container>
          <div
            // @ts-ignore
            css={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1rem',
            }}
          >
            <Products data={products} />
          </div>
        </>
      )}

      <Container text css={{ marginBottom: '1rem' }}>
        {unparsedPanels.map(panel => {
          const __html = panel?.text
            .replace(/<h2/g, '<h4')
            .replace(/<\/h2/g, '</h4')
            .replace(/\n\n/g, '<br/><br/>')

          if (!__html) return null

          return (
            <React.Fragment key={panel.panelName}>
              <Header as="h3">{panel.title}</Header>
              <Container
                text
                dangerouslySetInnerHTML={{
                  __html: `<div>${__html}</div>`,
                }}
                data-itsme={true}
                data-html={__html}
              />
            </React.Fragment>
          )
        })}
      </Container>
    </>
  )
}

export default Recall
